import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { createWorkOrderSchema } from './schema';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../hooks/useAuth';
import { Role } from '../../../types/auth';
import { modalIds } from '../../modals/SuperModal/types';
import { openModal } from '../../../state/actions/ui/modal';

// Sentry
import * as Sentry from '@sentry/react';

// Types
import { type WorkOrder } from '../../../types/workOrder';
import { AutoFillData, Props } from './types';

// Components
import { BasicInfo } from './BasicInfo';
import { Description } from './Description';
import { MainButton } from '../../inputs/buttons/MainButton';
import { useAutolocatePk } from '../../../hooks/useAutolocatePk';
import type { PkCandidate } from '../../../utils/resolvePkAtPosition';

// Styles
import { Typography } from '../../display/Typography';
import {
  ButtonsContainer,
  ContainerForm,
  FieldsContainer,
} from '../components/MainStyled';
import { ContainerWithButton, HeaderButtonsGroup } from './styles';
import { client } from '../../../gql/client';
import { GET_ORDER_BY_ID } from '../../../gql/queries/orders/orders';
import { GET_GENERIC_ORDER_BY_ID } from '../../../gql/queries/genericOrders';
import { GET_CAMPAIGN_BY_ID } from '../../../gql/queries/campaign';
import { formAccidentNaming } from '../AddBasicInfoAccidentForm/constants';
import { formOrderNaming } from './constants';

const NewWorkOrderForm = ({
  onFormSubmit,
  initialData,
  namingPersistForm,
  isCampaign = false,
  isAction = false,
  isAccident = false,
  registeredByRequired = false,
  prefillRegisteredBy = true,
  errorMessage,
  titleBasicInfo,
  titleDate,
  textDateHour,
}: Props) => {
  const { t } = useTranslation('forms');
  const { currentRole, user } = useAuth();
  const [switchState, setSwitchState] = useState<boolean>(false);
  const directionRequired = useFeatureFlag('directionRequired', true);
  // Mismo flag que gobierna el render del campo Margen en Description.tsx.
  const showMargin = useFeatureFlag('showMargin', true);

  const [autoFillData, setAutoFillData] = useState({});
  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<WorkOrder>({
    defaultValues: {
      ...initialData,
      // Operario que registra: preserva el valor persistido; si no hay y es
      // Actuación/Accidente, pre-rellena con el usuario logueado salvo rol
      // Tablet (cuenta compartida → obliga a elegir persona real).
      //
      // `prefillRegisteredBy` (2026-08-05, decisión del usuario): en EDICIÓN
      // el pre-relleno se apaga. Un valor vacío en un registro antiguo
      // significa que ese dato nunca se rellenó; rellenarlo solo porque
      // alguien abrió el formulario reescribe la autoría con quien lo tocó
      // después — y, de paso, dejaba el "obligatorio" en decorativo, porque
      // el hueco se tapaba solo antes de que el usuario lo viera.
      registeredByUserId:
        initialData.registeredByUserId ??
        (prefillRegisteredBy && (isAction || isAccident)
          ? currentRole !== Role.Tablet
            ? user?.user?.id ?? ''
            : ''
          : ''),
      intent: 'pass-data',
      isCampaignActive:
        initialData.isCampaignActive !== undefined
          ? initialData.isCampaignActive
          : isCampaign
            ? 'true'
            : 'false',
    },
    resolver: yupResolver(createWorkOrderSchema(directionRequired, showMargin)) as any,
    context: {
      switchState: switchState,
      isAction: isAction,
      isActionOrIsAccident: isAction || isAccident,
      registeredByRequired: registeredByRequired,
    },
  });

  useCacheFormInSessionStorage<WorkOrder>(namingPersistForm, control);

  const switchStateValue = () => {
    const { isCampaignActive } = initialData;

    if (isCampaignActive) {
      return isCampaignActive === 'true';
    }

    return isCampaign;
  };

  useLayoutEffect(() => {
    setSwitchState(switchStateValue());
  }, []);

  useEffect(() => {
    isAccident && setSwitchState(false);
  }, [isAccident]);

  const dispatch = useDispatch();
  const handleChange = () => {
    if (switchState) {
      dispatch(
        openModal(modalIds.CHANGE_CAMPAIGN_TO_OT_MODAL, {
          handleClickContinue: () => {
            setSwitchState(false);
            setValue('isCampaignActive', 'false');
            setValue('campaign', '');
          },
        })
      );
    } else {
      setSwitchState(true);
      setValue('isCampaignActive', 'true');
    }
  };

  const fetchDataToAutoFill = useCallback(async () => {
    const order = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: initialData.orderParentId,
      },
    });

    if (order.data.order.genericOrdersIDs.length > 0) {
      const genericOrderData = await client.query({
        query: GET_GENERIC_ORDER_BY_ID,
        variables: {
          id: order.data.order.genericOrdersIDs[0],
        },
      });

      return {
        order: order.data.order,
        genericOrder: genericOrderData.data.genericOrder,
      };
    }

    if (order.data.order.campaignIDs.length > 0) {
      const campaignData = await client.query({
        query: GET_CAMPAIGN_BY_ID,
        variables: {
          id: order.data.order.campaignIDs[0],
        },
      });
      return {
        order: order.data.order,
        campaignData: campaignData.data.campaign,
      };
    }
  }, [initialData]);

  // Autolocalizar PK: el operario está EN el sitio, así que el PK sale del GPS
  // en vez de teclearse. Quien lo ESCRIBE en el formulario es `Description`
  // —allí viven el `setValue` y la ref del desplegable de carretera—, así que
  // registra su función aquí al montarse.
  const applyAutolocateRef = useRef<((match: PkCandidate) => void) | null>(null);
  const autolocate = useAutolocatePk({
    getPreferredRoadId: () => getValues('road'),
    getConcessionId: () => getValues('concession'),
    onApply: (match) => applyAutolocateRef.current?.(match),
  });

  //TODO: review types
  const autoFillFunction = useCallback(async () => {
    try {
      const result = await fetchDataToAutoFill();
      setAutoFillData(result as AutoFillData);
    } catch (error) {
      Sentry.captureException(error);
      console.log('Error fetching data:', error);
    }
  }, [initialData]);

  return (
    <ContainerForm
      onSubmit={handleSubmit(onFormSubmit, (invalid) => {
        // DX (2026-07-04): sin esto el submit inválido era 100% silencioso —
        // los dropdowns marcan borde rojo pero campos como actionElementOrder
        // pueden fallar sin marca visible y el usuario no sabe por qué no avanza.
        console.warn('[NewWorkOrderForm] validación fallida:', Object.keys(invalid));
      })}
    >
      <FieldsContainer>
        <ContainerWithButton>
          <BasicInfo
            control={control}
            register={register}
            errors={errors}
            isAction={isAction}
            title={titleBasicInfo}
            titleDate={titleDate}
            textDateHour={textDateHour}
            showOperatorPicker={
              namingPersistForm === formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM
            }
            onOperatorChange={(name) => setValue('operator', name)}
          />
          {(isAction ||
            namingPersistForm === formAccidentNaming.NEW_ACCIDENT_FORM) && (
              <HeaderButtonsGroup>
                <MainButton
                  variant="secondary"
                  text={t('workOrder.autoFill')}
                  onClick={autoFillFunction}
                  type="button"
                />
                <MainButton
                  variant="secondary"
                  text={t('workOrder.autolocatePk')}
                  onClick={() => {
                    void autolocate.run();
                  }}
                  disabled={autolocate.isLoading}
                  type="button"
                />
              </HeaderButtonsGroup>
            )}
        </ContainerWithButton>
        <Description
          control={control}
          setValue={setValue}
          register={register}
          errors={errors}
          switchState={switchState}
          setSwitchState={handleChange}
          namingPersistForm={namingPersistForm}
          isAccident={isAccident}
          isAction={isAction}
          autoFillData={autoFillData}
          onRegisterAutolocate={(apply) => {
            applyAutolocateRef.current = apply;
          }}
        />
        {errorMessage && (
          <Typography colorText={'lightCoralRed'} size={'XS'}>
            {errorMessage}
          </Typography>
        )}
      </FieldsContainer>
      <ButtonsContainer>
        <MainButton id="save-order-button" value={'primary'} text={t('common:buttons.continue')} type="submit" />
      </ButtonsContainer>
    </ContainerForm>
  );
};

export default NewWorkOrderForm;
